/* eslint-disable @typescript-eslint/no-explicit-any */
import qs from 'qs'

import { Env } from 'config/Env'
import {
  LoginFormInput,
  LoginResponse,
  ForgotPasswordRequest,
  ForgotPasswordResponse,
  ChangePasswordRequest,
  ResetPasswordRequest,
  Profile,
  UserSettingInfo,
  UpdateNotifReq,
  MfaConfiguration,
  MfaCodeGenerationData,
  VerifyMfaOptionsRequest,
  UpdatePhoneNumberRequest,
  UpdateUserMfaPreferenceRequest,
  SaveTaskRuleLayoutConfig,
  LoginMfaCodeGenerationData,
  CommunityNotificationPreferenceRequest,
  SaveNotificationPreferencesRequest,
} from 'features/account/types'
import { AssignLeads, AssignListResponse } from 'features/referrals/types'
import { Envelope } from 'utils/types/Envelope'

import { HttpClient, ApiRequestTypes } from '../../../utils/apiService/httpService'

const LOGIN_BASE_URL = `${Env.REACT_APP_API_URL}Identity/api/TOKEN/TOKEN`
const FORGOT_PASSWORD_URL = `${Env.REACT_APP_API_URL}Identity/api/Account/ForgotPassword`
const CHANGE_PASSWORD_URL = `${Env.REACT_APP_API_URL}Identity/api/Account/ChangePassword`
const RESET_PASSWORD_URL = `${Env.REACT_APP_API_URL}Identity/api/Account/ResetPassword`

const GET_PROFILE_DETAILS_URL = `${Env.REACT_APP_API_URL}Identity/api/Profile/GetProfileDetails`
const UPDATE_PROFILE_URL = `${Env.REACT_APP_API_URL}Identity/api/Profile/UpdateProfileDetails`
const UPDATE_PROFILE_IMAGE_URL = `${Env.REACT_APP_API_URL}Identity/api/Profile/UploadProfileImage`
const GET_USER_SETTINGS = `${Env.REACT_APP_API_URL}Identity/api/Profile/GetUserSetting`
const UPDATE_USER_SETTINGS = `${Env.REACT_APP_API_URL}Identity/api/Profile/UpdateUserSettings`
const GET_NOTIFICATION_LIST = `${Env.REACT_APP_API_URL}Identity/api/ConfigurationSettings/GetAllNotificationConfiguration`
const UPDATE_NOTIFICATION_LIST = `${Env.REACT_APP_API_URL}Identity/api/ConfigurationSettings/SaveNotificationConfiguration`
const REFERRAL_ASSIGN_USERS_LIST = `${Env.REACT_APP_API_URL}Identity/api/User/GetAssignList`
const LOGOUT_URL = `${Env.REACT_APP_API_URL}Identity/api/Account/LogoutWeb`
const GET_MFA_CONFIGURATION = `${Env.REACT_APP_API_URL}Organization/api/Security/GetMfaConfigurationByOrganizationId`
const GET_CONFIGURE_MFA = `${Env.REACT_APP_API_URL}Organization/api/Security/ConfigureMfa`
const GET_USER_MFA_OPTIONS = `${Env.REACT_APP_API_URL}Identity/api/User/GetUserMfaOptions`
const GENERATE_MFA_CODE = `${Env.REACT_APP_API_URL}Identity/api/User/GenerateMfaCode`
const GENERATE_LOGIN_MFA_CODE = `${Env.REACT_APP_API_URL}Identity/api/User/GenerateLoginMfaCode`
const RESET_MFA = `${Env.REACT_APP_API_URL}Identity/api/User/ResetMFA`
const VERIFY_MFA_OPTIONS = `${Env.REACT_APP_API_URL}Identity/api/User/VerifyMfaOptions`
const UPDATE_PHONE_NUMBER = `${Env.REACT_APP_API_URL}Identity/api/User/UpdatePhoneNumber`
const UPDATE_USER_MFA_PREFERENCE = `${Env.REACT_APP_API_URL}Identity/api/User/UpdateUserMfaPreference`
const SAVE_RULE_CONFIGURATION = `${Env.REACT_APP_API_URL}Referral/api/RuleConfigurationSettings/Save`
const GET_ALL_RULE_CONFIGURATION = `${Env.REACT_APP_API_URL}Referral/api/RuleConfigurationSettings/GetAll`
const SSO_VALIDATED = `${Env.REACT_APP_API_URL}Identity/api/SSO/Authorize`

export const loginCall = (loginData: LoginFormInput): Promise<LoginResponse> =>
  HttpClient[ApiRequestTypes.post](LOGIN_BASE_URL, qs.stringify(loginData))

export const forgotPasswordCall = (
  ForgotPassword: ForgotPasswordRequest,
): Promise<ForgotPasswordResponse> =>
  HttpClient[ApiRequestTypes.post](FORGOT_PASSWORD_URL, ForgotPassword)

export const changePasswordCall = (
  ResetPassword: ChangePasswordRequest,
): Promise<ForgotPasswordResponse> =>
  HttpClient[ApiRequestTypes.post](CHANGE_PASSWORD_URL, ResetPassword)

export const resetPasswordCall = (
  ResetPassword: ResetPasswordRequest,
): Promise<ForgotPasswordResponse> =>
  HttpClient[ApiRequestTypes.post](RESET_PASSWORD_URL, ResetPassword)

export const fetchProfileDetails = (id: string): Promise<Envelope> =>
  HttpClient[ApiRequestTypes.get](`${GET_PROFILE_DETAILS_URL}?userId=${id}`)

export const updateProfile = (data: Profile): Promise<Envelope> =>
  HttpClient[ApiRequestTypes.post](UPDATE_PROFILE_URL, data)

export const updateProfileImage = (imageData: FormData): Promise<Envelope> =>
  HttpClient[ApiRequestTypes.post](UPDATE_PROFILE_IMAGE_URL, imageData)

export const fetchUserSettings = (id: string): Promise<Envelope> =>
  HttpClient[ApiRequestTypes.get](`${GET_USER_SETTINGS}?userId=${id}`)

export const updateUserSettings = (settingsData: UserSettingInfo): Promise<Envelope> =>
  HttpClient[ApiRequestTypes.post](UPDATE_USER_SETTINGS, settingsData)

export const fetchNotificationSettings = (): Promise<Envelope> =>
  HttpClient[ApiRequestTypes.get](GET_NOTIFICATION_LIST)
export const saveNotificationSettings = (data: UpdateNotifReq): Promise<Envelope> =>
  HttpClient[ApiRequestTypes.post](UPDATE_NOTIFICATION_LIST, data)

const GET_COMMUNITY_NOTIFICATION_CONFIG = `${Env.REACT_APP_API_URL}Identity/api/ConfigurationSettings/GetAllNotificationConfigurations`
export const fetchCommunityNotificationSettings = (
  data: CommunityNotificationPreferenceRequest,
): Promise<Envelope> => HttpClient[ApiRequestTypes.post](GET_COMMUNITY_NOTIFICATION_CONFIG, data)

// Confirmed live 2026-09-16 — write side of the community read above.
const SAVE_COMMUNITY_NOTIFICATION_PREFERENCES = `${Env.REACT_APP_API_URL}Identity/api/ConfigurationSettings/SaveNotificationPreferences`
export const saveCommunityNotificationPreferences = (
  data: SaveNotificationPreferencesRequest,
): Promise<Envelope> =>
  HttpClient[ApiRequestTypes.post](SAVE_COMMUNITY_NOTIFICATION_PREFERENCES, data)
export const fetchAssignUsersList = (requestBody: AssignLeads): Promise<AssignListResponse> =>
  HttpClient[ApiRequestTypes.post](REFERRAL_ASSIGN_USERS_LIST, requestBody)

export const logOut = (): Promise<Envelope> => HttpClient[ApiRequestTypes.post](LOGOUT_URL)
export const fetchMfaConfiguration = (orgId: string): Promise<Envelope> =>
  HttpClient[ApiRequestTypes.get](`${GET_MFA_CONFIGURATION}?OrganizationId=${orgId}`)
export const configureMfa = (data: MfaConfiguration): Promise<Envelope> =>
  HttpClient[ApiRequestTypes.post](GET_CONFIGURE_MFA, data)
export const fetchUserMfaOptions = (userId: string): Promise<Envelope> =>
  HttpClient[ApiRequestTypes.get](`${GET_USER_MFA_OPTIONS}?userId=${userId}`)
export const generateMfaCode = (data: MfaCodeGenerationData): Promise<Envelope> =>
  HttpClient[ApiRequestTypes.post](GENERATE_MFA_CODE, data)
export const generateLoginMfaCode = (data: LoginMfaCodeGenerationData): Promise<Envelope> =>
  HttpClient[ApiRequestTypes.post](GENERATE_LOGIN_MFA_CODE, qs.stringify(data))
export const resetMfa = (userId: string): Promise<Envelope> =>
  HttpClient[ApiRequestTypes.post](`${RESET_MFA}`, { userId })
export const verifyMfaOptions = (data: VerifyMfaOptionsRequest): Promise<Envelope> =>
  HttpClient[ApiRequestTypes.post](VERIFY_MFA_OPTIONS, data)
export const updatePhoneNumber = (data: UpdatePhoneNumberRequest): Promise<Envelope> =>
  HttpClient[ApiRequestTypes.post](UPDATE_PHONE_NUMBER, data)
export const updateUserMfaPreference = (data: UpdateUserMfaPreferenceRequest): Promise<Envelope> =>
  HttpClient[ApiRequestTypes.post](UPDATE_USER_MFA_PREFERENCE, data)

export const saveRuleConfiguration = (data: SaveTaskRuleLayoutConfig): Promise<Envelope> =>
  HttpClient[ApiRequestTypes.post](SAVE_RULE_CONFIGURATION, data)
export const getAllRuleConfiguration = (id: number | null): Promise<Envelope> =>
  HttpClient[ApiRequestTypes.post](GET_ALL_RULE_CONFIGURATION, { organizationId: id })
export const ssoTokenValidation = (idToken: string): Promise<LoginResponse> =>
  HttpClient[ApiRequestTypes.get](SSO_VALIDATED, {
    headers: {
      Authorization: `Bearer ${idToken}`,
      client_id: 'gccweb',
    },
  })
